import moment from 'moment';
import isEmpty from 'lodash/isEmpty';

import { v4 as uuidv4 } from 'uuid';

import { orderNonFoodPhysicalPackaging } from 'pages/new-evaluation-form/utils/orderEvalFormFields';

import {
  isFoodForm,
  isNonFoodForm,
} from 'pages/new-evaluation-form/utils/checkTypeEvaluationForm';

const COUNTRY_OF_ORIGIN = 'countryOfOrigin';

export const mappingValueEvaluationForm = ({
  isDatetime,
  isNumberType,
  value,
}) => {
  let mappingValue = null;

  if (isDatetime) {
    const isMomentType = moment.isMoment(value); // prevent bad data
    mappingValue = value && isMomentType ? value.format('MM/DD/YYYY') : '';
  } else if (isNumberType) {
    mappingValue = value.toFixed(2);
  } else if (typeof value === 'string') {
    mappingValue = value ? value.replace(/(?:\r\n|\r|\n)/g, '<br />') : '';
  } else {
    mappingValue = value;
  }

  return mappingValue;
};

const generateDataEachTable = (table = {}) => {
  let result = [];

  if (table) {
    for (const [name, value] of Object.entries(table)) {
      const rowData = {
        key: uuidv4(),
        headerName: name,
        property: {
          name,
        },
        ...value,
      };
      result.push(rowData);
    }
  }

  return result;
};

const mappingAllergensTable = (allergenDetails = []) => {
  if (allergenDetails?.length > 0) {
    return allergenDetails.map((item) => {
      const key = uuidv4();
      return {
        ...item,
        key: uuidv4(),
        property: {
          name: key,
        },
      };
    });
  }
  return [];
};

export const transformInitialEvaluationValuesFood = (
  initialValues,
  formAssetDetails
) => {
  let result = {};

  const productEvaForm = initialValues?.productEvaForm?.[0] ?? {};
  const mappingFormAssetDetails =
    transformEvalFormAssetDetails(formAssetDetails);

  const headerTable = [
    {
      key: uuidv4(),
      property: {
        name: 'productName',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.productName,
      competitiveBrand: productEvaForm?.competitiveBrand?.productName,
    },
    {
      key: uuidv4(),
      property: {
        name: 'upc',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.upc,
      competitiveBrand: productEvaForm?.competitiveBrand?.upc,
    },
    {
      key: uuidv4(),
      property: {
        name: 'dateLabelNomenclature',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.dateLabelNomenclature,
      competitiveBrand: productEvaForm?.competitiveBrand?.dateLabelNomenclature,
    },
    {
      key: uuidv4(),
      property: {
        name: 'overallOrganolepticScore',
      },
      privateBrand:
        productEvaForm?.privateBrandProduct?.overallOrganolepticScore ?? 0,
      competitiveBrand:
        productEvaForm?.competitiveBrand?.overallOrganolepticScore ?? 0,
    },
  ];

  const headerResult = [
    {
      key: uuidv4(),
      overallResult: initialValues?.overallResult,
      majorNonConformances: initialValues?.majorNonConformances,
      reviewer: initialValues?.reviewer,
      evaluationType: initialValues?.evaluationType,
      billingComments: initialValues?.billingComments,
      property: {
        name: 'headerResult',
      },
    },
  ];
  const headerImage = [
    {
      key: uuidv4(),
      privateProductFont:
        mappingFormAssetDetails[
          productEvaForm?.privateBrandProduct?.digitalAssetFrontId
        ] ?? null,
      competitiveProductFront:
        mappingFormAssetDetails[
          productEvaForm?.competitiveBrand?.digitalAssetFrontId
        ] ?? null,
    },
  ];

  const organolepticEvaluationSummary = generateDataEachTable(
    productEvaForm?.organolepticEvaluationSummary
  );

  const sampleInformation = generateDataEachTable(
    initialValues?.sampleInformation
  );

  const dietaryCertification = generateDataEachTable(
    initialValues?.dietaryCertification
  );

  const statement = generateDataEachTable(initialValues?.statement);

  const allergenDetails = mappingAllergensTable(
    productEvaForm?.allergenDetails
  );

  const physicalPackagingEvaluation = generateDataEachTable(
    transformInitialPhysicalPackagingTable(
      initialValues?.physicalPackagingEvaluation
    )
  );

  const analyticalTestingProduct = generateDataEachTable(
    productEvaForm?.analyticalTestingProduct?.analyticalTestingProductDetails
  );

  const microbiologicalTesting = generateDataEachTable(
    productEvaForm?.microbiologicalTesting
  );

  const productImagery = [
    transformInitialProductImagery(
      productEvaForm?.qaEvaluationFormAttachment,
      mappingFormAssetDetails
    ) ?? [],
  ];

  result = {
    ...result,
    headerTable,
    headerResult,
    headerImage,
    organolepticEvaluationSummary,
    sampleInformation,
    dietaryCertification,
    statement,
    allergenDetails,
    physicalPackagingEvaluation,
    analyticalTestingProduct,
    microbiologicalTesting,
    productImagery,
    qaEvaluationFormAssetDetails: transformEvalFormAssetDetails(
      formAssetDetails ?? []
    ),
  };

  return result;
};

export const transformInitialEvaluationValuesNonFood = (
  initialValues,
  formAssetDetails
) => {
  let result = {};

  const productEvaForm = initialValues?.productEvaForm?.[0] ?? {};
  const mappingFormAssetDetails =
    transformEvalFormAssetDetails(formAssetDetails);

  const headerTable = [
    {
      key: uuidv4(),
      property: {
        name: 'productName',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.productName,
      competitiveBrand: productEvaForm?.competitiveBrand?.productName,
    },
    {
      key: uuidv4(),
      property: {
        name: 'upc',
      },
      privateBrand: productEvaForm?.privateBrandProduct?.upc,
      competitiveBrand: productEvaForm?.competitiveBrand?.upc,
    },
  ];

  const headerResult = [
    {
      key: uuidv4(),
      overallResult: initialValues?.overallResult,
      majorNonConformances: initialValues?.majorNonConformances,
      reviewer: initialValues?.reviewer,
      evaluationType: initialValues?.evaluationType,
      billingComments: initialValues?.billingComments,
      property: {
        name: 'headerResult',
      },
    },
  ];
  const headerImage = [
    {
      key: uuidv4(),
      privateProductFont:
        mappingFormAssetDetails[
          productEvaForm?.privateBrandProduct?.digitalAssetFrontId
        ] ?? null,
      competitiveProductFront:
        mappingFormAssetDetails[
          productEvaForm?.competitiveBrand?.digitalAssetFrontId
        ] ?? null,
    },
  ];

  const sensoryEvaluationSummary = generateDataEachTable(
    productEvaForm?.sensoryEvaluationSummary
  );

  const sampleInformation = generateDataEachTable(
    initialValues?.sampleInformation
  );

  const allergenDetails = mappingAllergensTable(
    productEvaForm?.allergenDetails
  );

  const physicalPackagingEvaluation = generateDataEachTable(
    orderNonFoodPhysicalPackaging(
      transformInitialPhysicalPackagingTable(
        initialValues?.physicalPackagingEvaluation
      )
    )
  );

  const analyticalTestingProduct = generateDataEachTable(
    productEvaForm?.analyticalTestingProduct?.analyticalTestingProductDetails
  );

  const microbiologicalTesting = generateDataEachTable(
    productEvaForm?.microbiologicalTesting
  );

  const productImagery = [
    transformInitialProductImagery(
      productEvaForm?.qaEvaluationFormAttachment,
      mappingFormAssetDetails
    ) ?? [],
  ];

  result = {
    ...result,
    headerTable,
    headerResult,
    headerImage,
    sensoryEvaluationSummary,
    sampleInformation,
    allergenDetails,
    physicalPackagingEvaluation,
    analyticalTestingProduct,
    microbiologicalTesting,
    productImagery,
    qaEvaluationFormAssetDetails: transformEvalFormAssetDetails(
      formAssetDetails ?? []
    ),
  };

  return result;
};

export const transformEvalFormAssetDetails = (qaEvaluationFormAssetDetails) => {
  let result = {};

  if (qaEvaluationFormAssetDetails?.length > 0) {
    qaEvaluationFormAssetDetails.forEach((assetDetail) => {
      const { qaEvaluationFormAssetId } = assetDetail;

      result = {
        ...result,
        [qaEvaluationFormAssetId]: assetDetail,
      };
    });
  }

  return result;
};

const transformInitialEachPhotoTypeProduct = ({
  photo,
  mappingFormAssetDetails,
}) => {
  let result = {};

  if (isEmpty(photo)) return {};

  for (const imageItem of photo) {
    result = {
      ...result,
      [imageItem.index]: {
        ...imageItem,
        digitalAssetId: mappingFormAssetDetails[imageItem.digitalAssetId],
      },
    };
  }

  return result;
};

export const transformInitialProductImagery = (
  productImagery,
  mappingFormAssetDetails
) => {
  const privateBrandProduct = transformInitialEachPhotoTypeProduct({
    photo: productImagery?.privateBrandProduct,
    mappingFormAssetDetails,
  });

  const competitiveBrandProduct = transformInitialEachPhotoTypeProduct({
    photo: productImagery?.competitiveBrandProduct,
    mappingFormAssetDetails,
  });

  const comparativeAttachment = transformInitialEachPhotoTypeProduct({
    photo: productImagery?.comparativeAttachment,
    mappingFormAssetDetails,
  });

  return {
    privateBrandProduct,
    competitiveBrandProduct,
    comparativeAttachment,
  };
};

export const generateBrandTypeProduct = (headerTable, headerImage) => {
  let privateBrandProduct = {};
  let competitiveBrandProduct = {};

  const fields = [
    'productName',
    'upc',
    'dateLabelNomenclature',
    'overallOrganolepticScore',
  ];

  fields.forEach((field) => {
    const foundRow = headerTable.find(
      (row) => row?.property?.name?.toLowerCase() === field.toLowerCase()
    );

    if (foundRow) {
      privateBrandProduct = {
        ...privateBrandProduct,
        [field]: foundRow.privateBrand,
      };
      competitiveBrandProduct = {
        ...competitiveBrandProduct,
        [field]: foundRow.competitiveBrand,
      };
    }
  });

  return {
    privateBrandProduct: {
      ...privateBrandProduct,
      digitalAssetFrontId:
        headerImage?.[0]?.privateProductFont?.qaEvaluationFormAssetId ?? null,
    },
    competitiveBrandProduct: {
      ...competitiveBrandProduct,
      digitalAssetFrontId:
        headerImage?.[0]?.competitiveProductFront?.qaEvaluationFormAssetId ??
        null,
    },
  };
};

export const generateParamsForEachTable = (tableFormValues) => {
  let result = {};

  if (tableFormValues) {
    tableFormValues.forEach((formItem) => {
      const {
        property: { name },
        ...restValues
      } = formItem;
      result = {
        ...result,
        [name]: {
          ...restValues,
        },
      };
    });
  }

  return result;
};

const transformEachPhotoTypeBrand = (photo) => {
  if (isEmpty(photo)) return [];

  return photo.map((photo) => ({
    ...photo,
    digitalAssetId: photo?.digitalAssetId?.qaEvaluationFormAssetId ?? null,
  }));
};

const transformProductImageryParams = (productImagery) => {
  const privateBrandProduct = transformEachPhotoTypeBrand(
    Object.values(productImagery?.privateBrandProduct)
  );
  const competitiveBrandProduct = transformEachPhotoTypeBrand(
    Object.values(productImagery?.competitiveBrandProduct)
  );

  const comparativeAttachment = transformEachPhotoTypeBrand(
    Object.values(productImagery?.comparativeAttachment)
  );

  return {
    privateBrandProduct,
    competitiveBrandProduct,
    comparativeAttachment,
  };
};

const transformParamsEvalFoodForm = (formValues) => {
  let result = {};

  const { privateBrandProduct, competitiveBrandProduct: competitiveBrand } =
    generateBrandTypeProduct(formValues?.headerTable, formValues?.headerImage);

  result = {
    ...result,
    overallResult: formValues?.headerResult?.[0]?.overallResult,
    majorNonConformances: formValues?.headerResult?.[0]?.majorNonConformances,
    reviewer: formValues?.headerResult?.[0]?.reviewer,
    evaluationType: formValues?.headerResult?.[0]?.evaluationType,
    billingComments: formValues?.headerResult?.[0]?.billingComments,
    productEvaForm: [
      {
        formIndex: null,
        privateBrandProduct,
        competitiveBrand,
        organolepticEvaluationSummary: generateParamsForEachTable(
          formValues?.organolepticEvaluationSummary
        ),
        allergenDetails: [
          ...Object.values(
            generateParamsForEachTable(formValues?.allergenDetails)
          ),
        ],
        analyticalTestingProduct: {
          analyticalTestingProductDetails: [
            ...Object.values(
              generateParamsForEachTable(formValues?.analyticalTestingProduct)
            ),
          ],
        },
        microbiologicalTesting: generateParamsForEachTable(
          formValues?.microbiologicalTesting
        ),
        qaEvaluationFormAttachment: {
          ...transformProductImageryParams(formValues?.productImagery?.[0]),
        },
      },
    ],
    dietaryCertification: generateParamsForEachTable(
      formValues?.dietaryCertification
    ),
    physicalPackagingEvaluation: transformFormValuesPhysicalPackagingTable(
      generateParamsForEachTable(formValues?.physicalPackagingEvaluation)
    ),
    sampleInformation: generateParamsForEachTable(
      formValues?.sampleInformation
    ),
    statement: generateParamsForEachTable(formValues?.statement),
  };

  return result;
};

const transformParamsEvalNonFoodForm = (formValues) => {
  let result = {};

  const { privateBrandProduct, competitiveBrandProduct: competitiveBrand } =
    generateBrandTypeProduct(formValues?.headerTable, formValues?.headerImage);

  result = {
    ...result,
    overallResult: formValues?.headerResult?.[0]?.overallResult,
    majorNonConformances: formValues?.headerResult?.[0]?.majorNonConformances,
    reviewer: formValues?.headerResult?.[0]?.reviewer,
    evaluationType: formValues?.headerResult?.[0]?.evaluationType,
    billingComments: formValues?.headerResult?.[0]?.billingComments,
    productEvaForm: [
      {
        formIndex: null,
        privateBrandProduct,
        competitiveBrand,

        allergenDetails: [
          ...Object.values(
            generateParamsForEachTable(formValues?.allergenDetails)
          ),
        ],
        analyticalTestingProduct: {
          analyticalTestingProductDetails: [
            ...Object.values(
              generateParamsForEachTable(formValues?.analyticalTestingProduct)
            ),
          ],
        },
        microbiologicalTesting: generateParamsForEachTable(
          formValues?.microbiologicalTesting
        ),
        qaEvaluationFormAttachment: {
          ...transformProductImageryParams(formValues?.productImagery?.[0]),
        },
        sensoryEvaluationSummary: generateParamsForEachTable(
          formValues?.sensoryEvaluationSummary
        ),
      },
    ],
    sampleInformation: generateParamsForEachTable(
      formValues?.sampleInformation
    ),
    physicalPackagingEvaluation: transformFormValuesPhysicalPackagingTable(
      generateParamsForEachTable(formValues?.physicalPackagingEvaluation)
    ),
  };

  return result;
};

export const transformFormParamsEvalForm = (formValues, type) => {
  if (isFoodForm(type)) return transformParamsEvalFoodForm(formValues);

  if (isNonFoodForm(type)) return transformParamsEvalNonFoodForm(formValues);

  return {};
};

const transformInitialPhysicalPackagingTable = (dataTable) => {
  let result = {};

  if (dataTable) {
    for (const [key, value] of Object.entries(dataTable)) {
      // countryOfOrigin is array, so we need to transform
      if (key === COUNTRY_OF_ORIGIN && !isEmpty(value)) {
        const mappingValues = value.reduce((acc, item, index) => {
          acc[`${key}${index + 1}`] = { ...item };
          return acc;
        }, {});
        result = {
          ...result,
          ...mappingValues,
        };
      } else {
        result = {
          ...result,
          [key]: value,
        };
      }
    }
  }

  return result;
};

// countryOfOrigin = {}, {} -> [{}, {}]
const transformFormValuesPhysicalPackagingTable = (formValues) => {
  let countryOfOrigin = [];
  let result = {};

  if (formValues) {
    for (const [key, value] of Object.entries(formValues)) {
      // countryOfOrigin is array, so we need to transform
      if (key.includes(COUNTRY_OF_ORIGIN)) {
        countryOfOrigin.push(value);
      } else {
        result = {
          ...result,
          [key]: value,
        };
      }
    }
  }

  return {
    ...result,
    countryOfOrigin,
  };
};
